import { env } from '@/lib/env';
import { useSession } from '@/lib/session';
import { Badge, Button, Card, CardContent, Label } from '@iq/ui';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, MessageSquareWarning, Users, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface HealthResponse {
  status: 'ok' | 'degraded';
  dependencies: Record<string, 'ok' | 'degraded' | 'unknown'>;
}

async function fetchHealth(): Promise<HealthResponse> {
  const res = await fetch(`${env.VITE_API_BASE_URL}/health`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Health check failed: HTTP ${res.status}`);
  return (await res.json()) as HealthResponse;
}

export function AdminPanel() {
  const { t } = useTranslation();
  const session = useSession();
  const isAdmin = session.status === 'authenticated' && session.session.role === 'admin';
  const healthQuery = useQuery({
    queryKey: ['admin', 'health'],
    queryFn: fetchHealth,
    refetchInterval: 30_000,
  });

  return (
    <div className="flex flex-col gap-4" data-testid="settings-panel-admin">
      {isAdmin ? (
        <Card>
          <CardContent className="flex flex-col gap-3 py-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <Label className="text-sm font-medium">{t('settings.admin.users')}</Label>
              <p className="text-xs text-muted-foreground">{t('settings.admin.usersHint')}</p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full sm:w-auto"
              data-testid="settings-admin-users-link"
              onClick={() => {
                const tab = window.open('/admin/users', '_blank');
                if (tab) tab.opener = null;
              }}
            >
              <Users className="mr-2 h-4 w-4" />
              {t('settings.admin.openUsers')}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="flex flex-col gap-3 py-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Label className="text-sm font-medium">{t('settings.admin.feedback')}</Label>
            <p className="text-xs text-muted-foreground">{t('settings.admin.feedbackHint')}</p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full sm:w-auto"
            data-testid="settings-admin-feedback-link"
            onClick={() => {
              const tab = window.open('/admin/feedback', '_blank');
              if (tab) tab.opener = null;
            }}
          >
            <MessageSquareWarning className="mr-2 h-4 w-4" />
            {t('settings.admin.openFeedback')}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-3 py-6">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">{t('settings.admin.status')}</Label>
            {healthQuery.data?.status === 'ok' ? (
              <Badge variant="outline" className="border-emerald-500/40 text-emerald-600">
                <CheckCircle2 className="mr-1 size-3" />
                {t('settings.admin.statusOk')}
              </Badge>
            ) : healthQuery.data?.status === 'degraded' || healthQuery.isError ? (
              <Badge variant="outline" className="border-destructive/40 text-destructive">
                <XCircle className="mr-1 size-3" />
                {t('settings.admin.statusDown')}
              </Badge>
            ) : (
              <Badge variant="outline">{t('settings.admin.statusLoading')}</Badge>
            )}
          </div>
          {healthQuery.data?.dependencies ? (
            <ul className="flex flex-col gap-1.5 text-xs">
              {Object.entries(healthQuery.data.dependencies).map(([name, status]) => (
                <li key={name} className="flex items-center justify-between gap-2">
                  <span className="font-mono text-muted-foreground">{name}</span>
                  <span className={status === 'ok' ? 'text-emerald-600' : 'text-destructive'}>
                    {status === 'ok' ? '●' : '×'} {status}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-3 py-6">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">{t('settings.admin.auditLog')}</Label>
            <Badge variant="outline">{t('settings.comingSoon')}</Badge>
          </div>
          <p className="text-xs text-muted-foreground">{t('settings.admin.auditLogHint')}</p>
        </CardContent>
      </Card>
    </div>
  );
}
